import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import JsBarcode from 'jsbarcode';
import { QRCodeSVG } from 'qrcode.react';
import { getDocument } from 'pdfjs-dist/webpack';
import { ToWords } from 'to-words';

// Simple HTML escape to avoid breaking layout when interpolating arbitrary strings
const escapeHtml = value =>
  String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');

// Format gate pass address object into compact multi-line HTML
export const formatGatePassAddress = addr => {
  if (!addr) return '-';
  if (typeof addr === 'string') return escapeHtml(addr);
  try {
    const parts = [];
    const push = v => {
      if (v !== undefined && v !== null && String(v).trim() !== '') {
        parts.push(String(v).trim());
      }
    };

    // Name
    push(addr.name);

    // Address lines
    const line12 = [addr.address_line_1, addr.address_line_2]
      .filter(Boolean)
      .map(s => String(s).trim())
      .filter(s => s.length)
      .join(', ');
    push(line12);

    // City, State, Pincode
    const csp = [addr.city, addr.state, addr.pincode]
      .filter(Boolean)
      .map(s => String(s).trim())
      .filter(s => s.length)
      .join(', ');
    push(csp);

    // Country
    push(addr.country);

    // Contact
    const contact = [addr.phone, addr.email]
      .filter(Boolean)
      .map(s => String(s).trim())
      .filter(s => s.length)
      .join(' / ');
    push(contact);

    if (!parts.length) return '-';

    return parts.map(p => `<div>${escapeHtml(p)}</div>`).join('');
  } catch (e) {
    // Fallback to JSON string if unexpected shape
    try {
      return `<div>${escapeHtml(JSON.stringify(addr))}</div>`;
    } catch {
      return '-';
    }
  }
};

const amountInWords = amount => {
  try {
    const toWords = new ToWords({
      localeCode: 'en-IN',
      converterOptions: {
        currency: true,
      },
    });

    return toWords.convert(amount);
  } catch (error) {
    return amount;
  }
};

export const convertPdfToHtml = async pdfUrl => {
  try {
    const pdf = await getDocument(pdfUrl).promise;
    let htmlContent = '';

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale: 4 });

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      await page.render({
        canvasContext: context,
        viewport: viewport,
      }).promise;

      const image = canvas.toDataURL('image/png');

      htmlContent += `
          <div class="awb-page" ${
            pageNum > 1 ? 'style="page-break-before: always;"' : ''
          }>
            <img src="${image}" style="width: 100%; height: auto;" />
          </div>
        `;
    }

    return `
        <div class="awb-content" style="width: 90mm;">
          ${htmlContent}
        </div>
      `;
  } catch (error) {
    console.error('Error converting PDF to HTML', error);
  }
};

// Picklist
const createPicklistTemplate = (data, picklistCode) => {
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Picking List</title>
      <style>
        @page { 
          size: 100mm 150mm;
          margin: 5mm; 
        }
        
        @media print {
          html, body {
            width: 90mm;
            height: 140mm;
            margin: 0;
            padding: 0;
          }
        }
        
        body { 
          font-family: Arial, sans-serif; 
          margin: 0;
          padding: 0;
          font-size: 7px;
          width: 90mm;
        }
        
        table { 
          width: 100%; 
          border-collapse: collapse;
          page-break-inside: auto;
          table-layout: fixed;
        }
        
        tr { 
          page-break-inside: avoid; 
          page-break-after: auto; 
        }
        
        th, td { 
          border: 1px solid black; 
          padding: 1px; 
          text-align: center;
          font-size: 6px;
          line-height: 1.2;
          white-space: normal;
          word-wrap: break-word;
          word-break: break-all;
        }
        
        th { 
          background-color: #f4f4f4; 
          font-weight: bold;
        }
        
        .header h1 { 
          text-align: center; 
          margin: 0;
          font-size: 10px;
          font-weight: bold;
        }
        
        .header-info { 
          display: flex; 
          justify-content: space-between;
          align-items: center;
          font-size: 7px;
          margin-bottom: 3px;
        }
        
        .barcode-container { 
          display: flex; 
          flex-direction: column; 
          align-items: center;
        }
        
        .barcode { 
          height: 20px !important;
          width: 85% !important;
          margin: 0 auto;
        }
        
        .barcode-text { 
          font-family: "Courier New", monospace;
          font-size: 6px;
          text-align: center;
        }
        
        .header-barcode-text { 
          font-family: "Courier New", monospace;
          font-size: 9px;
          text-align: center;
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>Picking List</h1>
        <div class="header-info">
          <div>
            <div class="barcode-container">
              <div class="header-barcode-text">Picklist Code: ${picklistCode}</div>
              <svg class="barcode" id="picklist-barcode"></svg>
            </div>
          </div>
          <span>Total Items: ${data.length}</span>
          <span>Created: ${new Date().toLocaleString()}</span>
        </div>
      </div>
      <table>
        <thead>
          <tr>
            <th style="width: 5%;">No</th>
            <th style="width: 12%;">Brand Name</th>
            <th style="width: 20%;">SKU Name</th>
            <th style="width: 5%;">Qty</th>
            <th style="width: 12%;">Shelf Code</th>
            <th style="width: 28%;">Barcode</th>
            <th style="width: 17%;">SKU Code</th>
          </tr>
        </thead>
        <tbody>
          ${data
            .map(
              (item, index) => `
            <tr>
              <td>${index + 1}</td>
              <td>${item.product.brand.name || '-'}</td>
              <td>${item.product.name || '-'}</td>
              <td>${item?.shipment_line_summary?.total_quantity || 0}</td>
              <td>${item?.shelf?.code || '-'}</td>
              <td>
                ${
                  item.product.barcode
                    ? `
                  <div class="barcode-container">
                    <svg class="barcode" id="barcode-${index}"></svg>
                    <div class="barcode-text">${item.product.barcode}</div>
                  </div>
                `
                    : '-'
                }
              </td>
              <td>${item.product.sku}</td>
            </tr>
          `,
            )
            .join('')}
        </tbody>
      </table>
    </body>
    </html>
  `;
};

const generatePicklistBarcodes = (newTab, data, picklistCode) => {
  const barcodeOptions = {
    format: 'CODE128',
    width: 0.8,
    height: 20,
    displayValue: false,
    margin: 0,
  };

  JsBarcode(
    newTab.document.querySelector('#picklist-barcode'),
    picklistCode,
    barcodeOptions,
  );

  data.forEach((item, index) => {
    if (item.product.barcode) {
      JsBarcode(
        newTab.document.querySelector(`#barcode-${index}`),
        item.product.barcode,
        barcodeOptions,
      );
    }
  });
};

export const generatePicklistPdf = (data, picklistCode) => {
  const content = createPicklistTemplate(data, picklistCode);

  openAndPrint(
    content,
    { data, picklistCode },
    (newTab, { data, picklistCode }) => {
      generatePicklistBarcodes(newTab, data, picklistCode);
    },
  );
};

// Batch-mode Picklist (one row per shipment, with cart partition numbers)
const createBatchPicklistTemplate = (data, picklistCode, batchPickingUrl) => {
  const shipments = data?.results || [];
  const totalPartitions = data?.total_partitions || 0;
  const qrMarkup = batchPickingUrl
    ? renderToStaticMarkup(
        React.createElement(QRCodeSVG, {
          value: batchPickingUrl,
          size: 70,
          marginSize: 1,
        }),
      )
    : '';

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Picking List</title>
      <style>
        @page {
          size: 100mm 150mm;
          margin: 5mm;
        }

        @media print {
          html, body {
            width: 90mm;
            height: 140mm;
            margin: 0;
            padding: 0;
          }
        }

        body {
          font-family: Arial, sans-serif;
          margin: 0;
          padding: 0;
          font-size: 7px;
          width: 90mm;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          page-break-inside: auto;
          table-layout: fixed;
        }

        tr {
          page-break-inside: avoid;
          page-break-after: auto;
        }

        th, td {
          border: 1px solid black;
          padding: 1px;
          text-align: center;
          font-size: 6px;
          line-height: 1.2;
          white-space: normal;
          word-wrap: break-word;
          word-break: break-all;
        }

        th {
          background-color: #f4f4f4;
          font-weight: bold;
        }

        .partition-no {
          font-size: 12px;
          font-weight: bold;
          font-family: "Courier New", monospace;
        }

        .summary-cell {
          text-align: left;
          word-break: break-word;
        }

        .header h1 {
          text-align: center;
          margin: 0;
          font-size: 10px;
          font-weight: bold;
        }

        .header-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 7px;
          margin-bottom: 3px;
        }

        .barcode-container {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .barcode {
          height: 20px !important;
          width: 85% !important;
          margin: 0 auto;
        }

        .barcode-text {
          font-family: "Courier New", monospace;
          font-size: 6px;
          text-align: center;
        }

        .header-barcode-text {
          font-family: "Courier New", monospace;
          font-size: 9px;
          text-align: center;
        }

        .qr-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1px;
        }

        .qr-label {
          font-size: 6px;
          font-weight: bold;
          text-align: center;
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>Picking List (Batch Mode)</h1>
        <div class="header-info">
          <div>
            <div class="barcode-container">
              <div class="header-barcode-text">Picklist Code: ${escapeHtml(
                picklistCode,
              )}</div>
              <svg class="barcode" id="picklist-barcode"></svg>
            </div>
          </div>
          <span>Shipments: ${shipments.length}</span>
          <span><b>Partitions: ${totalPartitions}</b></span>
          <span>Created: ${new Date().toLocaleString()}</span>
          ${
            qrMarkup
              ? `
          <div class="qr-container">
            ${qrMarkup}
            <div class="qr-label">Scan to start<br/>Batch Picking</div>
          </div>
          `
              : ''
          }
        </div>
      </div>
      <table>
        <thead>
          <tr>
            <th style="width: 8%;">Part.</th>
            <th style="width: 20%;">Order ID</th>
            <th style="width: 15%;">Shipment</th>
            <th style="width: 26%;">Barcode</th>
            <th style="width: 7%;">Qty</th>
            <th style="width: 24%;">Summary</th>
          </tr>
        </thead>
        <tbody>
          ${shipments
            .map(
              (item, index) => `
            <tr>
              <td class="partition-no">${item.partition_number ?? '-'}</td>
              <td>${escapeHtml(item.channel_order_id || '-')}</td>
              <td>${escapeHtml(item.shipment_code || '-')}</td>
              <td>
                ${
                  item.shipment_code
                    ? `
                  <div class="barcode-container">
                    <svg class="barcode" id="shipment-barcode-${index}"></svg>
                    <div class="barcode-text">${escapeHtml(
                      item.shipment_code,
                    )}</div>
                  </div>
                `
                    : '-'
                }
              </td>
              <td>${item.quantity ?? 0}</td>
              <td class="summary-cell">${(item.summary || [])
                .map(s => `${escapeHtml(s.sku)} x ${s.quantity}`)
                .join('<br/>')}</td>
            </tr>
          `,
            )
            .join('')}
        </tbody>
      </table>
    </body>
    </html>
  `;
};

const generateBatchPicklistBarcodes = (newTab, data, picklistCode) => {
  const barcodeOptions = {
    format: 'CODE128',
    width: 0.8,
    height: 20,
    displayValue: false,
    margin: 0,
  };

  JsBarcode(
    newTab.document.querySelector('#picklist-barcode'),
    picklistCode,
    barcodeOptions,
  );

  (data?.results || []).forEach((item, index) => {
    if (item.shipment_code) {
      JsBarcode(
        newTab.document.querySelector(`#shipment-barcode-${index}`),
        item.shipment_code,
        barcodeOptions,
      );
    }
  });
};

export const generateBatchPicklistPdf = (
  data,
  picklistCode,
  batchPickingUrl,
) => {
  const content = createBatchPicklistTemplate(
    data,
    picklistCode,
    batchPickingUrl,
  );

  openAndPrint(
    content,
    { data, picklistCode },
    (newTab, { data, picklistCode }) => {
      generateBatchPicklistBarcodes(newTab, data, picklistCode);
    },
  );
};

// Invoice
const createInvoiceTemplate = invoiceData => {
  const formatDate = dateString => {
    const date = new Date(dateString);
    return date
      .toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
      .replace(/ /g, '-');
  };

  const currency = invoiceData.order.payment_currency || 'INR';

  const isIGST = invoiceData.order.gst_type === 'igst';

  const totalQuantity = invoiceData.invoice_lines.reduce(
    (sum, line) => sum + (line.quantity || 0),
    0,
  );

  const totalDiscount = invoiceData.invoice_lines.reduce(
    (sum, line) => sum + (parseFloat(line.total_discount) || 0),
    0,
  );

  const sumOfInvoiceLineTaxableValue = invoiceData.invoice_lines.reduce(
    (sum, line) => sum + (parseFloat(line.total_rate) || 0),
    0,
  );

  const sumOfInvoiceLineIGST = invoiceData.invoice_lines.reduce(
    (sum, line) => sum + (parseFloat(line.total_igst) || 0),
    0,
  );

  const sumOfInvoiceLineTotalCGST = invoiceData.invoice_lines.reduce(
    (sum, line) => sum + (parseFloat(line.total_cgst) || 0),
    0,
  );

  const sumOfInvoiceLineSGST = invoiceData.invoice_lines.reduce(
    (sum, line) => sum + (parseFloat(line.total_sgst) || 0),
    0,
  );

  const sumOfInvoiceLineAmount = invoiceData.invoice_lines.reduce(
    (sum, line) => sum + (parseFloat(line.total_amount) || 0),
    0,
  );

  // Total 에 보여줄 값 계산
  const shippingTaxableValue =
    parseFloat(invoiceData.shipment_extra_tax.shipping_charges_net) || 0;
  const codTaxableValue =
    parseFloat(invoiceData.shipment_extra_tax.cash_on_delivery_charges_net) ||
    0;
  const totalTaxableValue =
    sumOfInvoiceLineTaxableValue + shippingTaxableValue + codTaxableValue;

  const shippingIGST =
    parseFloat(invoiceData.shipment_extra_tax.shipping_charges_igst) || 0;
  const codIGST =
    parseFloat(invoiceData.shipment_extra_tax.cash_on_delivery_charges_igst) ||
    0;
  const totalIGST = sumOfInvoiceLineIGST + shippingIGST + codIGST;

  const shippingCGST =
    parseFloat(invoiceData.shipment_extra_tax.shipping_charges_cgst) || 0;
  const codCGST =
    parseFloat(invoiceData.shipment_extra_tax.cash_on_delivery_charges_cgst) ||
    0;
  const totalCGST = sumOfInvoiceLineTotalCGST + shippingCGST + codCGST;

  const shippingSGST =
    parseFloat(invoiceData.shipment_extra_tax.shipping_charges_sgst) || 0;
  const codSGST =
    parseFloat(invoiceData.shipment_extra_tax.cash_on_delivery_charges_sgst) ||
    0;
  const totalSGST = sumOfInvoiceLineSGST + shippingSGST + codSGST;

  const shippingCharges =
    parseFloat(invoiceData.shipment.shipping_charges) || 0;
  const codCharges =
    parseFloat(invoiceData.shipment.cash_on_delivery_charges) || 0;
  const totalAmount = sumOfInvoiceLineAmount + shippingCharges + codCharges;

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Tax Invoice</title>
      <style>
        @page { 
          size: 100mm 150mm;
          margin: 5mm;
        }

        @media print {
          html, body {
            width: 90mm;
            height: 140mm;
            margin: 0;
            padding: 0;
          }
          body { 
            font-family: Arial, sans-serif;
            font-size: 8px;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
        
        body { 
          font-family: Arial, sans-serif; 
          margin: 0;
          padding: 4px;
          font-size: 8px;
          width: 90mm;
        }
        
        .main-container {
          border: 1px solid #000;
          width: 100%;
          max-width: 90mm;
        }
        
        .header-container {
          position: relative;
          display: flex;
          align-items: center;
          margin-bottom: 4px;
        }
  
        .header-logo {
          position: absolute;
          left: 0;
          margin: 0;
        }
        
        .header-title {
          width: 100%;
          text-align: center;
          font-size: 10px;
          font-weight: bold;
        }
        
        .flex-row {
          display: flex;
          border-bottom: 1px solid #000;
        }
        
        .flex-row:last-child {
          border-bottom: none;
        }
        
        .flex-cell {
          flex: 1;
          padding: 3px;
          border-right: 1px solid #000;
          word-wrap: break-word;
          word-break: break-word;
          min-width: 0;
        }
        
        .flex-cell:last-child {
          border-right: none;
        }
        
        .name {
          font-weight: bold;
          margin-bottom: 2px;
        }
        
        .bold {
          font-weight: bold;
        }
        
        .address-line {
          margin-bottom: 1px;
          line-height: 1.2;
        }
        
        .products-table {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
        }
        
        .products-table th, 
        .products-table td {
          border: 1px solid #000;
          padding: 2px;
          text-align: left;
          word-wrap: break-word;
          word-break: break-word;
          font-size: 7px;
        }
        
        .text-right { text-align: right; }
        .text-center { text-align: center; }
        
        .barcode-container {
          text-align: center;
          margin: 2px 0;
        }
        
        .barcode-container svg {
          height: 15px !important;
          max-width: 80% !important;
          margin: 0 auto;
        }
        
        .barcode-label {
          font-size: 7px;
          margin-top: 1px;
          font-family: "Courier New", monospace;
        }
        
        .amount-section {
          display: flex;
          border-top: 1px solid #000;
        }
        
        .amount-section > div:first-child {
          flex: 3;
          padding: 3px;
          border-right: 1px solid #000;
        }
        
        .amount-section > div:last-child {
          flex: 1;
          padding: 3px;
          text-align: center;
          word-break: break-word;
          word-wrap: break-word;
        }
        
        .amount-section p {
          margin: 0;
        }
        
        .signature-image {
          max-width: 100px;
        }
        
        .bill-by {
          padding: 3px;
          border-top: 1px solid #000;
          display: flex;
          flex-direction: column;
          align-items: center;
          font-size: 7px;
        }
      </style>
    </head>
    <body>
      <div class="header-container">
        <div class="header-logo">
          <img src="${
            invoiceData.order.warehouse.logo_image
          }" alt="Logo" style="height: 15px;"/>
        </div>      
        <div class="header-title">Tax Invoice</div>
      </div>

      <div class="main-container">
        <!-- Header Row -->
        <div class="flex-row">
          <div class="flex-cell">
            <div class="name">${invoiceData.order.warehouse.party_name}</div>
            <div class="address-line">${
              invoiceData.order.warehouse.address_line_1
            }</div>
            <div class="address-line">${
              invoiceData.order.warehouse.address_line_2
            }</div>
            <div class="address-line">${
              invoiceData.order.warehouse.address_city
            } - ${invoiceData.order.warehouse.address_pincode}</div>
            <div class="address-line">${
              invoiceData.order.warehouse.address_state
            }, ${invoiceData.order.warehouse.address_country}</div>
            <div class="address-line">Ph No: ${
              invoiceData.order.warehouse.address_phone
            }</div>
            ${
              invoiceData.fssai
                ? `<div class="address-line">${invoiceData.fssai}</div>`
                : ''
            }
          </div>

          <div class="flex-cell">
            <div class="text-center">
              <div class="bold">Invoice Code:</div>
              ${invoiceData.shipment.invoice_code}
              <div class="barcode-container">
                <svg id="invoice-barcode"></svg>
                <div class="barcode-label">${
                  invoiceData.shipment.invoice_code
                }</div>
              </div>
              Order No: ${invoiceData.order.channel_order_id}
              <div class="barcode-container">
                <svg id="shipment-barcode"></svg>
                <div class="barcode-label">${
                  invoiceData.shipment.shipment_code
                }</div>
              </div>
              <div>Order Date: ${formatDate(invoiceData.order.created)}</div>
            </div>
          </div>
          
          <div class="flex-cell">
            <div>Invoice Date:</div>
            <div class="bold">${formatDate(invoiceData.shipment.invoice_date)}
            </div>
            
            <div>Portal:</div>
            <div class="bold">${invoiceData.order.channel.channel_type}</div>
            
            <div>Payment Mode:</div>
            <div class="bold">${invoiceData.order.payment_type.toUpperCase()}</div>
          </div>
        </div>

        <!-- Address Row -->
        <div class="flex-row">
          <div class="flex-cell">
            <div class="address-block">
              <strong>Bill To:</strong><br/>
              <div class="name">${invoiceData.billing_address.name}</div>
              <div class="address-line">${
                invoiceData.billing_address.address_line_1
              }</div>
              <div class="address-line">${
                invoiceData.billing_address.address_line_2
              }</div>
              <div class="address-line">${invoiceData.billing_address.city} - ${
    invoiceData.billing_address.pincode
  }</div>
              <div class="address-line">${invoiceData.billing_address.state}, ${
    invoiceData.billing_address.country
  }</div>
              <div class="address-line">T: ${
                invoiceData.billing_address.phone || ''
              }</div>
            </div>
          </div>
          <div class="flex-cell">
            <div class="address-block">
              <strong>Ship To:</strong><br/>
              <div class="name">${invoiceData.shipping_address.name}</div>
              <div class="address-line">${
                invoiceData.shipping_address.address_line_1
              }</div>
              <div class="address-line">${
                invoiceData.shipping_address.address_line_2
              }</div>
              <div class="address-line">${
                invoiceData.shipping_address.city
              } - ${invoiceData.shipping_address.pincode}</div>
              <div class="address-line">${
                invoiceData.shipping_address.state
              }, ${invoiceData.shipping_address.country}</div>
              <div class="address-line">T: ${
                invoiceData.shipping_address.phone || ''
              }</div>
            </div>
          </div>
          <div class="flex-cell">
            <div>Dispatch Through</div>
            <div class="bold">${
              invoiceData.shipment.shipping_provider?.shipping_provider_type ||
              ''
            }</div>
            <div>AWB No</div>
            <div class="bold">${invoiceData.shipment.awb_number || ''}</div>
            <div class="barcode-container">
              <svg id="awb-barcode"></svg>
              <div class="barcode-label">${
                invoiceData.shipment.awb_number || ''
              }</div>
            </div>
          </div>
        </div>

        <!-- Products Section -->
        <div style="padding: 8px;">
          <table class="products-table">
            <thead>
              <tr>
                <th style="width: 5%;">Sr No.</th>
                <th style="width: 20%;">Product Name</th>
                <th style="width: 15%;">Product Code</th>
                <th style="width: 5%;">Qty</th>
                <th style="width: 9%;">MRP</th>
                <th style="width: 9%;">Rate</th>
                ${
                  totalDiscount > 0
                    ? '<th style="width: 9%;">Discount</th>'
                    : ''
                }
                <th style="width: 9%;">Taxable Value (${currency})</th>
                ${
                  isIGST
                    ? '<th style="width: 9%;">IGST (' + currency + ')</th>'
                    : '<th style="width: 9%;">CGST (' +
                      currency +
                      ')</th><th style="width: 9%;">SGST (' +
                      currency +
                      ')</th>'
                }
                <th style="width: 10%;">Amount (${currency})</th>
              </tr>
            </thead>
            <tbody>
              ${invoiceData.invoice_lines
                .map(
                  (line, index) => `
                <tr>
                  <td class="text-center">${index + 1}</td>
                  <td>${line.product_name}</td>
                  <td>${line.product_sku}</td>
                  <td class="text-center">${line.quantity}</td>
                  <td class="text-center">${line.channel_product_mrp}</td>
                  <td class="text-center">${line.unit_rate}</td>
                  ${
                    totalDiscount > 0
                      ? `<td class="text-center">${line.total_discount}</td>`
                      : ''
                  }
                  <td class="text-center">${line.total_rate}</td>
                  ${
                    isIGST
                      ? `<td class="text-center">
                          <div>${line.total_igst}</div>
                          <div>(${Number(line.product_tax_rate).toFixed(
                            1,
                          )}%)</div>
                         </td>`
                      : `<td class="text-center">
                           <div>${line.total_cgst}</div>
                           <div>(${(Number(line.product_tax_rate) / 2).toFixed(
                             1,
                           )}%)</div>
                         </td>
                         <td class="text-center">
                           <div>${line.total_sgst}</div>
                           <div>(${(Number(line.product_tax_rate) / 2).toFixed(
                             1,
                           )}%)</div>
                         </td>`
                  }
                  <td class="text-center">${line.total_amount}</td>
                </tr>
              `,
                )
                .join('')}
              ${
                Number(invoiceData.shipment.shipping_charges) > 0
                  ? `<tr>
                      <td colspan="3" class="text-right"><strong>Shipping Charges</strong></td>
                      <td></td>
                      <td colspan="${
                        totalDiscount > 0 ? '3' : '2'
                      }" class="text-center"></td>
                      <td class="text-center">${
                        invoiceData.shipment_extra_tax.shipping_charges_net
                      }</td>
                      ${
                        isIGST
                          ? `<td class="text-center">${invoiceData.shipment_extra_tax.shipping_charges_igst}</td>`
                          : `<td class="text-center">${invoiceData.shipment_extra_tax.shipping_charges_cgst}</td>
                             <td class="text-center">${invoiceData.shipment_extra_tax.shipping_charges_sgst}</td>`
                      }
                      <td class="text-center">${
                        invoiceData.shipment.shipping_charges
                      }</td>
                    </tr>`
                  : ''
              }
              ${
                Number(invoiceData.shipment.cash_on_delivery_charges) > 0
                  ? `<tr>
                      <td colspan="3" class="text-right"><strong>COD Charges</strong></td>
                      <td></td>
                      <td colspan="${
                        totalDiscount > 0 ? '3' : '2'
                      }" class="text-center"></td>
                      <td class="text-center">${
                        invoiceData.shipment_extra_tax
                          .cash_on_delivery_charges_net
                      }</td>
                      ${
                        isIGST
                          ? `<td class="text-center">${invoiceData.shipment_extra_tax.cash_on_delivery_charges_igst}</td>`
                          : `<td class="text-center">${invoiceData.shipment_extra_tax.cash_on_delivery_charges_cgst}</td>
                             <td class="text-center">${invoiceData.shipment_extra_tax.cash_on_delivery_charges_sgst}</td>`
                      }
                      <td class="text-center">${
                        invoiceData.shipment.cash_on_delivery_charges
                      }</td>
                    </tr>`
                  : ''
              }
              <tr>
                <td colspan="3" class="text-right"><strong>Total:</strong></td>
                <td class="text-center">${totalQuantity}</td>
                <td colspan="${totalDiscount > 0 ? '3' : '2'}"></td>
                <td class="text-center">${totalTaxableValue.toFixed(2)}</td>
                ${
                  isIGST
                    ? `<td class="text-center">${totalIGST.toFixed(2)}</td>`
                    : `<td class="text-center">${totalCGST.toFixed(2)}</td>
                  <td class="text-center">${totalSGST.toFixed(2)}</td>`
                }
                <td class="text-center">${totalAmount.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Amount Section -->
        <div class="amount-section">
          <div>
            <strong>Amount Chargeable (in words)</strong><br/>
            <div class="bold">${amountInWords(totalAmount)}</div>
            <div class="bold">Tax is payable on reverse charge basis: No</div>
            <br/>
            <strong>Declaration</strong>
            <p>1. This is a computer generated Invoice. Doesn't require signature or stamp.</p>
            <p>2. All figures are showing in INR.</p>
            <p>3. Ship/Handling Charges inclusive of GST.</p>
            <p>4. All Disputes are subject to jurisdiction only.</p>
          </div>
          <div>
            <div class="bold">For ${
              invoiceData.order.warehouse.party_name
            }</div>
            <div style="margin-top: 10px;">
              <img src="${
                invoiceData.order.warehouse.signature_image
              }" alt="Signature" class="signature-image"/>
              <div style="font-size: 8px;">Authorised Signatory</div>
            </div>
          </div>
        </div>

        <!-- Bill By -->
        <div class="bill-by text-center">
          Bill By:
          <div>This is a computer generated Invoice</div>
        </div>
      </div>
    </body>
    </html>
  `;
};

const generateInvoiceBarcodes = (newTab, invoiceData) => {
  const barcodeOptions = {
    format: 'CODE128',
    width: 0.6,
    height: 15,
    displayValue: false,
    margin: 0,
  };

  // Invoice barcode
  JsBarcode(
    newTab.document.querySelector('#invoice-barcode'),
    invoiceData.shipment.invoice_code,
    barcodeOptions,
  );

  // Shipment barcode
  JsBarcode(
    newTab.document.querySelector('#shipment-barcode'),
    invoiceData.shipment.shipment_code,
    barcodeOptions,
  );

  // AWB barcode
  if (invoiceData.shipment.awb_number) {
    JsBarcode(
      newTab.document.querySelector('#awb-barcode'),
      invoiceData.shipment.awb_number,
      barcodeOptions,
    );
  }
};

export const generateInvoicePdf = invoiceData => {
  const content = createInvoiceTemplate(invoiceData);

  openAndPrint(content, invoiceData, (newTab, data) => {
    generateInvoiceBarcodes(newTab, data);
  });
};

// Manifest
const createManifestTemplate = (
  data,
  manifestCode,
  shippingProvider,
  channel,
) => {
  return `
    <!DOCTYPE html>
    <html>
      <head>
        <title>Manifest</title>
        <style>
          @page { 
            size: 150mm 100mm;
            margin: 5mm; 
          }
          
          @media print {
            html, body {
              width: 140mm;
              height: 90mm;
              margin: 0;
              padding: 0;
            }
          }
          
          body { 
            font-family: Arial, sans-serif; 
            margin: 0;
            padding: 0;
            font-size: 7px;
            width: 140mm;
          }
          
          .header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 8px;
            margin-bottom: 2px;
          }
          
          .manifest-table {
            width: 100%;
            border-collapse: collapse;
            page-break-inside: auto;
            table-layout: fixed;
          }
          
          tr { 
            page-break-inside: avoid; 
            page-break-after: auto; 
          }
          
          th, td { 
            border: 1px solid black; 
            padding: 1px; 
            text-align: left;
            font-size: 6px;
            white-space: normal;
            word-wrap: break-word;
            word-break: break-all;
          }
        
          th { 
            background-color: #f4f4f4; 
            font-weight: bold;
          }
          
          .barcode-container { 
            display: flex; 
            flex-direction: column; 
            align-items: center;
          }
          
          .barcode { 
            height: 15px !important;
            width: 90% !important;
            margin: 0;
          }
          
          .barcode-text { 
            font-family: "Courier New", monospace;
            font-size: 6px;
            text-align: center;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div>Manifest No: ${manifestCode}</div>
          <div>Shipping Provider: ${shippingProvider}</div>
          <div>Channel: ${channel}</div>
        </div>
  
        <table class="manifest-table">
          <thead>
            <tr>
              <th style="width: 10%;">Airwaybill</th>
              <th style="width: 8%;">Reference Number</th>
              <th style="width: 10%;">Attention</th>
              <th style="width: 8%;">Address3</th>
              <th style="width: 5%;">Pincode</th>
              <th style="width: 16%;">Contents</th>
              <th style="width: 5%;">Weight</th>
              <th style="width: 8%;">Declared Value</th>
              <th style="width: 8%;">Collectable</th>
              <th style="width: 6%;">Mode</th>
              <th style="width: 16%;">Barcode</th>
            </tr>
          </thead>
          <tbody>
            ${data
              .map((shipment, index) => {
                const declaredValue = (
                  parseFloat(shipment.total_gross_amount) +
                  parseFloat(shipment.shipping_charges) +
                  parseFloat(shipment.cash_on_delivery_charges)
                ).toFixed(2);

                const isCOD = shipment.order.payment_type === 'cod';
                const collectableValue = isCOD ? declaredValue : '0.0';
                const mode = isCOD ? 'COD' : 'NON COD';

                return `
                <tr>
                  <td>${shipment.awb_number}</td>
                  <td>${shipment.order.channel_order_id}</td>
                  <td>${shipment.order.shipping_address.name}</td>
                  <td>${shipment.order.shipping_address.city}</td>
                  <td>${shipment.order.shipping_address.pincode}</td>
                  <td>${shipment.product_summary[0].product_name}</td>
                  <td>${shipment.weight.value} (${shipment.weight.unit})
                  </td>
                  <td>${declaredValue}</td>
                  <td>${collectableValue}</td>
                  <td>${mode}</td>
                  <td>
                    <div class="barcode-container">
                      <svg class="barcode" id="barcode-${index}"></svg>
                      <div class="barcode-text">${shipment.awb_number}</div>
                    </div>
                  </td>
                </tr>
              `;
              })
              .join('')}
          </tbody>
        </table>
      </body>
    </html>
  `;
};

const generateManifestBarcodes = (newTab, data) => {
  const barcodeOptions = {
    format: 'CODE128',
    width: 0.8,
    height: 20,
    displayValue: false,
    margin: 0,
  };

  data.forEach((shipment, index) => {
    JsBarcode(
      newTab.document.querySelector(`#barcode-${index}`),
      shipment.awb_number,
      barcodeOptions,
    );
  });
};

export const generateManifestPdf = (
  data,
  manifestCode,
  shippingProvider,
  channel,
) => {
  const content = createManifestTemplate(
    data,
    manifestCode,
    shippingProvider,
    channel,
  );

  openAndPrint(content, data, (newTab, data) => {
    generateManifestBarcodes(newTab, data);
  });
};

// Return Manifest
const createReturnManifestTemplate = (
  data,
  returnManifestCode,
  createdDate,
) => {
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Return Manifest</title>
      <style>
        @page { 
          size: 100mm 150mm;
          margin: 5mm; 
        }
        
        @media print {
          html, body {
            width: 90mm;
            height: 140mm;
            margin: 0;
            padding: 0;
          }
        }
        
        body { 
          font-family: Arial, sans-serif; 
          margin: 0;
          padding: 0;
          font-size: 7px;
          width: 90mm;
        }
        
        .header h1 { 
          text-align: center; 
          margin: 0;
          font-size: 10px;
          font-weight: bold;
        }
        
        .header-info { 
          display: flex; 
          justify-content: space-between;
          align-items: center;
          font-size: 7px;
          margin-bottom: 2px;
        }
        
        table { 
          width: 100%; 
          border-collapse: collapse;
          page-break-inside: auto;
          table-layout: fixed;
        }
        
        tr { 
          page-break-inside: avoid; 
          page-break-after: auto; 
        }
        
        th, td { 
          border: 1px solid black;
          padding: 1px;
          text-align: left;
          font-size: 6px;
          line-height: 1.2;
          white-space: normal;
          word-wrap: break-word;
          word-break: break-all;
        }
        
        th { 
          background-color: #f4f4f4; 
          font-weight: bold;
        }
        
        th:first-child,
        td:first-child {
          text-align: center;
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>Return Manifest</h1>
        <div class="header-info">
          <span>Return manifest code: ${returnManifestCode}</span>
          
          <span>Manifest creation date: ${new Date(
            createdDate,
          ).toLocaleDateString('en-IN', {
            timeZone: 'Asia/Kolkata',
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
          })}</span>
          
        </div>
      </div>
      <table>
        <thead>
          <tr>
            <th style="width: 5%;">No</th>
            <th style="width: 25%;">Shipment Code</th>
            <th style="width: 20%;">Order No</th>
            <th style="width: 25%;">Invoice No</th>
            <th style="width: 25%;">AWB No.</th>
          </tr>
        </thead>
        <tbody>
          ${data
            .map(
              (item, index) => `
            <tr>
              <td>${index + 1}</td>
              <td>${item.shipment_code || '-'}</td>
              <td>${item.order?.channel_order_id || '-'}</td>
              <td>${item.invoice_code || '-'}</td>
              <td>${item.awb_number || '-'}</td>
            </tr>
          `,
            )
            .join('')}
        </tbody>
      </table>
    </body>
    </html>
  `;
};

export const generateReturnManifestPdf = (
  data,
  returnManifestCode,
  returnManifestCreatedDate,
) => {
  const content = createReturnManifestTemplate(
    data,
    returnManifestCode,
    returnManifestCreatedDate,
  );

  openAndPrint(content);
};

const createBlueDartAwbTemplate = data => {
  const sumOfInvoiceLineAmount = data.invoice_lines.reduce(
    (sum, line) => sum + (parseFloat(line.total_amount) || 0),
    0,
  );

  const shippingCharges = parseFloat(data.shipment.shipping_charges) || 0;
  const codCharges = parseFloat(data.shipment.cash_on_delivery_charges) || 0;
  const totalAmount = sumOfInvoiceLineAmount + shippingCharges + codCharges;

  return `
    <div class="bluedart-awb-section">
      <style>
        .bluedart-awb-section {
          font-family: Arial, sans-serif; 
          font-size: 8px;
          width: 90mm;
        }
        
        .bluedart-awb-section * {
          box-sizing: border-box;
        }

        .bluedart-awb-section .awb-box {
          border: 1px solid #000;
          margin-bottom: 2px;
        }

        .bluedart-awb-section .awb-header {
          display: flex;
        }

        .bluedart-awb-section .header-left {
          flex: 1;
          padding: 8px;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .bluedart-awb-section .header-left img {
          height: 30px;
          width: auto;
        }

        .bluedart-awb-section .header-right {
          flex: 3;
          padding: 8px;
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          align-items: center;
          text-align: center;
        }

        .bluedart-awb-section .header-title {
          font-weight: bold;
          font-size: 12px;
          margin-bottom: 2px;
        }

        .bluedart-awb-section .provider-name {
          margin-bottom: 4px;
        }

        .bluedart-awb-section .barcode-container {
          width: 100%;
          margin-top: 4px;
        }

        .bluedart-awb-section .barcode {
          height: 40px !important;
          width: 100% !important;
        }

        .bluedart-awb-section .main-content {
          display: flex;
          border-top: 1px solid #000;
        }

        .bluedart-awb-section .left-column,
        .bluedart-awb-section .right-column {
          flex: 1;
          padding: 8px;
        }

        .bluedart-awb-section .left-column {
          border-right: 1px solid #000;
        }

        .bluedart-awb-section .column-title {
          font-weight: bold;
          margin-bottom: 8px;
        }

        .bluedart-awb-section .address-content {
          line-height: 1.4;
        }

        .bluedart-awb-section .info-item {
          margin-bottom: 4px;
        }

        .bluedart-awb-section .sender-box {
          border: 1px solid #000;
          padding: 6px;
          margin: 8px 0;
        }

        .bluedart-awb-section .package-info {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 8px;
          border-top: 1px solid #000;
        }

        .bluedart-awb-section .details-section {
          margin-top: 10px;
        }

        .bluedart-awb-section .commodity,
        .bluedart-awb-section .delivery-details,
        .bluedart-awb-section .signature-section,
        .bluedart-awb-section .warehouse-address-section {
          border-top: 1px solid #000;
          padding: 8px;
        }
        
        .bluedart-awb-section .warehouse-address-section p {
          margin-top: 0;
          font-weight: bold;
        }
      </style>

      <div class="awb-box">
        <div class="awb-header">
          <div class="header-left">
            <img src="${data.order.warehouse.logo_image}" alt="Logo" />
          </div>
          <div class="header-right">
            <div class="header-title">${
              data.shipment.shipping_provider.shipping_provider_type
            }</div>
            <div class="provider-name">${
              data.shipment.shipping_provider.shipping_provider_type
            }</div>
            <div class="barcode-container">
              <svg id="awb-barcode-blue-dart" class="barcode"></svg>
              <div>*${data.shipment.awb_number}*</div>
            </div>
          </div>
        </div>

        <div class="main-content">
          <div class="left-column">
            <div class="column-title">Name & Delivery Address</div>
            <div class="address-content">
              ${data.shipping_address.name}<br/>
              ${data.shipping_address.address_line_1}<br/>
              ${data.shipping_address.address_line_2}<br/>
              ${data.shipping_address.city} - ${
    data.shipping_address.pincode
  }(${data.shipment?.bluedart_tracking_code || '-'})<br/>
              ${data.shipping_address.state}<br/>
              ${data.shipping_address.country}<br/>
              Contact Number: ${data.shipping_address.phone}
            </div>
          </div>

          <div class="right-column">
            <div class="column-title">Payment Info</div>
            <div class="info-item">Payment Mode: ${
              data.order.payment_type === 'cod' ? 'COD' : 'PREPAID'
            }</div>
            <div class="info-item">Order No: ${
              data.order.channel_order_id
            }</div>
            <div class="info-item">Channel Name: ${
              data.order.channel.channel_type
            }</div>
            <div class="info-item">Declared Value: ${totalAmount.toFixed(
              2,
            )}</div>
            <div class="info-item">Collectable Value: ${
              data.order.payment_type === 'cod' ? totalAmount.toFixed(2) : '0.0'
            }</div>
          </div>
        </div>

        <div class="package-info">
          <span>No. of Boxes: 1</span>
          <span>Weight: ${data.shipment.weight?.value}(${
    data.shipment.weight?.unit
  })</span>
          <span>Dimensions: ${(
            parseFloat(data.shipment.package_width) / 10
          ).toFixed(1)} * ${(
    parseFloat(data.shipment.package_length) / 10
  ).toFixed(1)} * ${(parseFloat(data.shipment.package_height) / 10).toFixed(
    1,
  )} Cms</span>
        </div>
        
        <div class="commodity">Commodity: Cosmetic Item</div>
        
        <div class="delivery-details">Delivery Details:</div>
        
        <div class="signature-section">
          Delivery Date:<br/>
          Time:<br/>
          Name:<br/>
          Consignee Signature:
        </div>
        
        <div class="warehouse-address-section">
          <p>If Undelivered, please return to :</p>
          ${data.order.warehouse.party_name}<br/>
          ${data.order.warehouse.address_line_1}<br/>
          ${data.order.warehouse.address_line_2}<br/>
          ${data.order.warehouse.address_city} - ${
    data.shipping_address.pincode
  }<br/>
          ${data.order.warehouse.address_state}, ${
    data.order.warehouse.address_country
  }<br/>
           Contact: ${data.order.warehouse.address_phone}
        </div>
      </div>
    </div>
  </div>
  `;
};

// invoice & Bluedart AWB Barcode
const generateInvoiceAndAwbBarcodes = (newTab, data) => {
  const barcodeOptions = {
    format: 'CODE128',
    width: 0.6,
    height: 15,
    displayValue: false,
    margin: 0,
  };

  // Generate invoice related barcodes
  JsBarcode(
    newTab.document.querySelector('#invoice-barcode'),
    data.shipment.invoice_code,
    barcodeOptions,
  );

  JsBarcode(
    newTab.document.querySelector('#shipment-barcode'),
    data.shipment.shipment_code,
    barcodeOptions,
  );

  JsBarcode(
    newTab.document.querySelector('#awb-barcode'),
    data.shipment.awb_number,
    barcodeOptions,
  );

  // Generate BlueDart AWB barcode
  JsBarcode(
    newTab.document.querySelector('#awb-barcode-blue-dart'),
    data.shipment.awb_number,
    {
      ...barcodeOptions,
      width: 2,
      height: 40,
    },
  );
};

export const generateBlueDartAwbPdf = data => {
  const invoiceContent = createInvoiceTemplate(data);
  const awbContent = createBlueDartAwbTemplate(data);

  const combinedContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Invoice & AWB</title>
      <style>
        @page { 
          size: 100mm 150mm;
          margin: 5mm;
        }
        
        @media print {
          html, body {
            width: 90mm;
            height: 140mm;
            margin: 0;
            padding: 0;
          }
          .page-break { 
            page-break-before: always;
          }
        }
        
        body { 
          margin: 0;
          padding: 0;
          width: 90mm;
        }
      </style>
    </head>
    <body>
      <div id="invoice-section">
        ${invoiceContent}
      </div>
      
      <div class="page-break">
        ${awbContent}
      </div>
    </body>
    </html>
  `;

  openAndPrint(combinedContent, data, (newTab, data) => {
    generateInvoiceAndAwbBarcodes(newTab, data);
  });
};

export const showAwbPdf = (awbHtml, invoiceData = null) => {
  if (invoiceData) {
    const content = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice & AWB</title>
        <style>
          @page { 
            size: 100mm 150mm;
            margin: 5mm;
          }
          
          @media print {
            html, body {
              width: 90mm;
              height: 140mm;
              margin: 0;
              padding: 0;
            }
            .page-break { 
              page-break-before: always;
            }
          }
          
          body { 
            margin: 0;
            padding: 0;
            width: 90mm;
          }
        </style>
      </head>
      <body>
        ${createInvoiceTemplate(invoiceData)}
        <div class="page-break">
          ${awbHtml}
        </div>
      </body>
      </html>
    `;

    openAndPrint(content, invoiceData, (printWindow, data) => {
      generateInvoiceBarcodes(printWindow, data);
    });
  } else {
    const content = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>AWB</title>
        <style>
          @page { 
            size: 100mm 150mm;
            margin: 5mm;
          }
          
          @media print {
            html, body {
              width: 90mm;
              height: 140mm;
              margin: 0;
              padding: 0;
            }
          }
          
          body { 
            margin: 0;
            padding: 0;
            width: 90mm;
          }
        </style>
      </head>
      <body>
        ${awbHtml}
      </body>
      </html>
    `;

    openAndPrint(content);
  }
};

// Delivery Challan
const createDeliveryChallanTemplate = gatePassData => {
  const {
    warehouse,
    document_code,
    gate_pass_code,
    document_data,
    created,
    gate_pass_type,
  } = gatePassData || {};
  const { lines = [] } = document_data || {};

  const formatDate = dateString => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const parseNumber = val => {
    if (!val) return 0;
    // 숫자, 소수점, 마이너스만 남기기
    return Number(String(val).replace(/[^0-9.-]+/g, '')) || 0;
  };

  // Indian numbering format with 2 decimal places (e.g., 12,34,567.89)
  const formatIndianNumber = num => {
    const n = Number(num) || 0;
    return n.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // 합계 계산 (필드명 정확히 맞추기)
  const totalQty = lines.reduce(
    (sum, line) => sum + (Number(line.quantity) || 0),
    0,
  );
  const totalRate = lines.reduce(
    (sum, line) => sum + parseNumber(line.rate),
    0,
  );
  const totalTaxableValue = lines.reduce(
    (sum, line) => sum + parseNumber(line.taxable_value),
    0,
  );
  const totalAmount = lines.reduce(
    (sum, line) => sum + parseNumber(line.amount),
    0,
  );

  // Format totals using Indian numbering system with 2 decimals
  const formattedTotalRate = formatIndianNumber(totalRate);
  const formattedTotalTaxableValue = formatIndianNumber(totalTaxableValue);
  const formattedTotalAmount = formatIndianNumber(totalAmount);

  const totalAmountInWords = amountInWords(totalAmount);

  return `
  <!DOCTYPE html>
  <html>
  <head>
    <title>${
      gate_pass_type === 'internal_consumption'
        ? 'Internal Consumption'
        : 'Delivery Challan'
    }</title>
    <style>
      /* Print setup */
      @page { size: A4; margin: 10mm; }

      /* Screen baseline */
      html, body { height: 100%; }
      body {
        font-family: Arial, sans-serif;
        font-size: 12px;
        color: #000;
        background: #f0f2f5; /* subtle gray so A4 stands out on screen */
        margin: 0;
        padding: 20px 0; /* breathing room around the A4 sheet on screen */
      }

      /* A4 sheet container for screen preview */
      .a4-page {
        width: 210mm;           /* A4 width */
        min-height: 297mm;      /* A4 height */
        margin: 0 auto;         /* center horizontally */
        background: #fff;       /* white page */
        box-shadow: 0 0 0.5cm rgba(0,0,0,0.15);
        box-sizing: border-box;
        padding: 10mm;          /* mimic print margins on screen */
      }

      /* Element styles inside the challan */
      .header {
        text-align: center;
        font-weight: bold;
        margin-bottom: 10px;
      }
      .header img {
        height: 40px;
        vertical-align: middle;
      }
      .meta {
        text-align: center;
        font-size: 11px;
        margin-bottom: 5px;
      }
      table {
        border-collapse: collapse;
        width: 100%;
      }
      th, td {
        border: 1px solid #000;
        padding: 6px;
        text-align: center;
        font-size: 11px;
      }
      th {
        background-color: #f2f2f2;
        font-weight: bold;
      }
      .info-table td {
        text-align: left;
        vertical-align: top;
      }
      .info-table .half {
        width: 50%;
      }
      .font-bold {
        font-weight: bold;
      }
      .footer {
        display: flex;
        justify-content: space-between;
        margin-top: 25px;
      }
      .signature {
        text-align: center;
        width: 40%;
      }
      .signature img {
        height: 40px;
      }
      .small {
        font-size: 11px;
      }

      /* Print overrides: remove screen-only cosmetics */
      @media print {
        body { background: #fff; padding: 0; }
        .a4-page { box-shadow: none; width: auto; min-height: auto; padding: 0; margin: 0; }
        th, td { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      }
    </style>
  </head>
  <body>
    <div class="a4-page">
      <div class="header">
      <img src="${warehouse?.logo_image || ''}" alt="logo"/><br/>
      ${
        gate_pass_type === 'internal_consumption'
          ? 'INTERNAL CONSUMPTION'
          : 'DELIVERY CHALLAN'
      }
    </div>
    <div class="meta">
      Document Code: ${document_code || ''} |
      GatePass: ${gate_pass_code || ''} |
      Date: ${formatDate(created)}
    </div>

    <table class="info-table">
      <tr>
        <td ${document_data?.recipient ? 'class="half"' : ''}>
          <div class="font-bold">Sender (From)</div>
          <div>${document_data?.sender?.party_name || ''}</div>
          <div>Billing Address :</div>
          <div>${formatGatePassAddress(
            document_data?.sender?.gate_pass_document_billing_address,
          )}</div>
          <div>Shipping Address :</div>
          <div>${formatGatePassAddress(
            document_data?.sender?.gate_pass_document_shipping_address,
          )}</div>
          <div>GSTIN: ${document_data?.sender?.gstin || '-'}</div>
          <div>Phone: ${document_data?.sender?.address_phone || ''}</div>
        </td>
        ${
          document_data?.recipient
            ? `
        <td class="half">
          <div class="font-bold">Recipient (To)</div>
          <div>${document_data?.recipient?.party_name || ''}</div>
          <div>Billing Address :</div>
          <div>${formatGatePassAddress(
            document_data?.recipient?.gate_pass_document_billing_address,
          )}</div>
          <div>Shipping Address :</div>
          <div>${formatGatePassAddress(
            document_data?.recipient?.gate_pass_document_shipping_address,
          )}</div>
          <div>GSTIN: ${document_data?.recipient?.gstin || '-'}</div>
          <div>Phone: ${document_data?.recipient?.address_phone || ''}</div>
        </td>
        `
            : ''
        }
      </tr>
    </table>

    <table style="margin-top: 10px;">
      <thead>
        <tr>
          <th style="width:5%;">No.</th>
          <th style="width:25%;">Description of Goods</th>
          <th style="width:12%;">SKU</th>
          <th style="width:10%;">Shelf</th>
          <th style="width:8%;">Qty</th>
          <th style="width:15%;">Unit Price</th>
          <th style="width:15%;">Taxable Value</th>
          <th style="width:15%;">Amount</th>
        </tr>
      </thead>
      <tbody>
        ${lines
          .map(
            (line, i) => `
          <tr>
            <td>${i + 1}</td>
            <td>${line?.name || ''}</td>
            <td>${line?.sku || ''}</td>
            <td>${line?.shelf_code || ''}</td>
            <td>${line?.quantity || ''}</td>
            <td>${line?.rate || ''}</td>
            <td>${line?.taxable_value || ''}</td>
            <td>${line?.amount || ''}</td>
          </tr>
        `,
          )
          .join('')}
        <tr>
          <td colspan="4" style="text-align:right;"><strong>Total</strong></td>
          <td><strong>${totalQty}</strong></td>
          <td><strong>₹${formattedTotalRate}</strong></td>
          <td><strong>₹${formattedTotalTaxableValue}</strong></td>
          <td><strong>₹${formattedTotalAmount}</strong></td>
          
        </tr>
      </tbody>
    </table>

    <div style="margin-top:15px;">
      <div>Amount Chargeable (in words)</div>
      <div><strong>${totalAmountInWords}</strong></div>
    </div>

    <div class="footer">
      <div></div>
      <div class="signature">
        <div>For ${warehouse?.party_name || ''}</div>
        <img src="${warehouse?.signature_image || ''}" alt="Signature"/><br/>
        <div class="small">Authorised Signatory</div>
      </div>
    </div>
    </div>
  </body>
  </html>
  `;
};

export const generateDeliveryChallanPdf = gatePassData => {
  const content = createDeliveryChallanTemplate(gatePassData);
  openAndPrint(content);
};

// PO
const createPOTemplate = poData => {
  const formatIndianNumber = (num, decimalPlaces = 2) => {
    if (num === undefined || num === null) return '';
    return parseFloat(num).toLocaleString('en-IN', {
      maximumFractionDigits: decimalPlaces,
      minimumFractionDigits: decimalPlaces,
    });
  };

  const formatDate = dateString => {
    const date = new Date(dateString);
    return date
      .toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
      .replace(/ /g, '-');
  };

  const totalQuantity = poData?.invoice_data.invoice_lines.reduce(
    (sum, line) => sum + (line?.quantity || 0),
    0,
  );

  const totalTaxableAmount = poData?.invoice_data.invoice_lines.reduce(
    (sum, line) => sum + (parseFloat(line.taxable_amount) || 0),
    0,
  );

  const totalIGSTAmount = poData?.invoice_data.invoice_lines.reduce(
    (sum, line) => sum + (parseFloat(line.igst_tax_amount) || 0),
    0,
  );

  const totalCGSTAmount = poData?.invoice_data.invoice_lines.reduce(
    (sum, line) => sum + (parseFloat(line.cgst_tax_amount) || 0),
    0,
  );

  const totalSGSTAmount = poData?.invoice_data.invoice_lines.reduce(
    (sum, line) => sum + (parseFloat(line.sgst_tax_amount) || 0),
    0,
  );

  const totalInvoiceValue =
    totalTaxableAmount + totalIGSTAmount + totalCGSTAmount + totalSGSTAmount;

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>PO</title>
      <style>
        @page { 
          size: A4;
          margin: 5mm; 
        }
        
        @media print {
          html, body {
            width: 210mm;
            height: 297mm;
            margin: 0;
            padding: 0;
          }
          body { 
            font-family: Arial, sans-serif;
            font-size: 12px;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
        
        body { 
          font-family: Arial, sans-serif; 
          margin: 0;
          padding: 0;
          font-size: 12px;
          width: 210mm;
          max-width: 100%;
        }
        
        .main-container {
          border: 1px solid #000;
          width: 100%;
        }
        
        .po-header {
          text-align: center;
          border-bottom: 1px solid #000;
          padding: 10px 0;
          display: flex;
          justify-content: center;
          align-items: center;
        }
        
        .po-header h5 {
          font-size: 15px;
          font-weight: bold;
          margin: 0;
        }
        
        .service-header {
          text-align: center;
          border-bottom: 1px solid #000;
          padding: 8px;
          font-weight: bold;
        }
        
        table {
          width: 100%;
          border-collapse: collapse;
        }
        
        td {
          padding: 4px 8px;
          vertical-align: top;
        }
        
        .half-width {
          width: 50%;
        }
        
        .border-right {
          border-right: 1px solid #000;
        }
        
        .border-bottom {
          border-bottom: 1px solid #000;
        }
        
        .items-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 0;
        }
        
        .items-table th,
        .items-table td {
          border: 1px solid #000;
          padding: 5px;
          font-size: 12px;
          text-align: center;
          vertical-align: middle;
        }
        
        .items-table th {
          background-color: #f2f2f2;
          font-weight: bold;
        }
        
        .items-table td.amount-cell {
          text-align: right;
        }
        
        .items-table td.service-cell {
          text-align: left;
        }
        
        .summary-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 0;
        }
        
        .summary-table td {
          border: 1px solid #000;
          padding: 4px 8px;
        }
        
        .summary-table td.summary-label {
          text-align: left;
          border-right: none;
        }
        
        .summary-table td.summary-value {
          text-align: right;
          border-left: none;
        }
        
        .summary-table td.summary-bold {
          font-weight: bold;
        }
        
        .text-center {
          text-align: center;
        }
        
        .text-right {
          text-align: right;
        }
        
        .text-left {
          text-align: left;
        }
        
        .font-bold {
          font-weight: bold;
        }
        
        .total-words {
          padding: 8px 8px 30px;
          border-bottom: 1px solid #000;
        }
        
        .combined-footer-container {
          display: flex;
          align-items: flex-start;
          padding: 8px;
        }
        
        .info-content {
          width: 70%;
          font-size: 10px;
        }
        
        .info-content p {
          margin: 0;
        }
        
        .signature-container {
          width: 30%;
          text-align: right;
        }
        
        .signature-image {
          max-width: 80px;
          height: 50px;
        }
        
        .signature-details {
          font-size: 11px;
        }
      </style>
    </head>
    <body>
      <div class="main-container">
        <div class="po-header">
          <h5>${poData?.invoice_data?.warehouse?.party_name} PO #${
    poData?.po_code
  }</h5>
        </div>
            
        <table>
          <tr>
            <td class="half-width border-right service-header">Buyer's Details</td>
            
            <td class="half-width service-header">Supplier's Details</td>
          </tr>
          
          <tr>
            <td class="half-width border-right">
              <div class="font-bold">${
                poData?.invoice_data.warehouse?.party_name || ''
              }</div>
              <div>Address : ${
                poData?.invoice_data.warehouse?.purchase_order_invoice_address
                  ?.address_line_1
              }</div>
              <div>${
                poData?.invoice_data.warehouse?.purchase_order_invoice_address
                  ?.address_line_2 || ''
              }</div>
              <div>${
                poData?.invoice_data.warehouse?.purchase_order_invoice_address
                  ?.city
              }, ${
    poData?.invoice_data.warehouse?.purchase_order_invoice_address?.state
  }, ${
    poData?.invoice_data.warehouse?.purchase_order_invoice_address?.pincode
  }</div>
              <div>State : ${
                poData?.invoice_data.warehouse?.purchase_order_invoice_address
                  ?.state
              }</div>
              <div>Contact No : ${
                poData?.invoice_data.warehouse?.purchase_order_invoice_address
                  ?.phone || '-'
              }</div>
              <div>Email ID : ${
                poData?.invoice_data.warehouse?.purchase_order_invoice_address
                  ?.email || '-'
              }</div>
              <div>GSTIN : ${poData?.invoice_data.warehouse?.gstin || '-'}</div>
            </td>
            
            <td class="half-width">
              <div class="font-bold">${poData?.invoice_data.vendor?.name}</div>
              <div>Address : ${
                poData?.invoice_data.vendor?.contact_address_line_1
              }</div>
              <div>${
                poData?.invoice_data.vendor?.contact_address_line_2 || ''
              }</div>
              <div>${poData?.invoice_data.vendor?.contact_city}, ${
    poData?.invoice_data.vendor?.contact_state
  }, ${poData?.invoice_data.vendor?.contact_pincode}</div>
              <div>State : ${poData?.invoice_data.vendor?.contact_state}</div>
              <div>Contact No : ${
                poData?.invoice_data.vendor?.contact_phone || '-'
              }</div>
              <div>Email ID : ${
                poData?.invoice_data.vendor?.contact_email || '-'
              }</div>
              <div>GSTIN : ${poData?.invoice_data.vendor?.gstin || '-'}</div>
            </td>
          </tr>
        </table>
        
        <table class="items-table">
          <tr>
            <th rowspan="2" style="width: 5%;">Sr.No</th>
            <th rowspan="2" style="width: 18%;">Description of Goods</th>
            <th rowspan="2" style="width: 9%;">SKU</th>
            <th rowspan="2" style="width: 8%;">HSN for service</th>
            <th rowspan="2" style="width: 5%;">Qty</th>
            <th rowspan="2" style="width: 5%;">Units</th>
            <th rowspan="2" style="width: 8%;">Taxable Amount</th>
            <th colspan="2" style="width: 14%;">SGST</th>
            <th colspan="2" style="width: 14%;">CGST</th>
            <th colspan="2" style="width: 14%;">IGST</th>
          </tr>
          <tr>
            <th style="width: 7%;">Rate</th>
            <th style="width: 7%;">Amount</th>
            <th style="width: 7%;">Rate</th>
            <th style="width: 7%;">Amount</th>
            <th style="width: 7%;">Rate</th>
            <th style="width: 7%;">Amount</th>
          </tr>
          ${poData?.invoice_data.invoice_lines
            .map(
              (line, index) => `
            <tr>
              <td>${index + 1}</td>
              <td class="service-cell">${line.name || ''}</td>
              <td>${line.sku || ''}</td>
              <td>${line.hsn_code || ''}</td>
              <td class="amount-cell">${formatIndianNumber(
                line?.quantity,
                0,
              )}</td>
              <td>Pcs</td>
              <td class="amount-cell">${formatIndianNumber(
                line.taxable_amount,
              )}</td>
              <td class="amount-cell">${parseFloat(
                line.sgst_tax_rate || 0,
              ).toFixed(0)}%</td>
              <td class="amount-cell">${formatIndianNumber(
                line.sgst_tax_amount,
              )}</td>
              <td class="amount-cell">${parseFloat(
                line.cgst_tax_rate || 0,
              ).toFixed(0)}%</td>
              <td class="amount-cell">${formatIndianNumber(
                line.cgst_tax_amount,
              )}</td>
              <td class="amount-cell">${parseFloat(
                line.igst_tax_rate || 0,
              ).toFixed(0)}%</td>
              <td class="amount-cell">${formatIndianNumber(
                line.igst_tax_amount,
              )}</td>
            </tr>
          `,
            )
            .join('')}
          <tr>
            <td colspan="4" class="service-cell font-bold">Total</td>
            <td class="amount-cell">${formatIndianNumber(totalQuantity, 0)}</td>
            <td></td>
            <td class="amount-cell">${formatIndianNumber(
              totalTaxableAmount,
            )}</td>
            <td></td>
            <td class="amount-cell">${formatIndianNumber(totalSGSTAmount)}</td>
            <td></td>
            <td class="amount-cell">${formatIndianNumber(totalCGSTAmount)}</td>
            <td></td>
            <td class="amount-cell">${formatIndianNumber(totalIGSTAmount)}</td>
          </tr>
        </table>
        
        <table class="summary-table">
          <tr>
            <td class="summary-label" style="width: 50%;"></td>
            <td class="summary-label" style="width: 25%;">Taxable Amount</td>
            <td class="summary-value" style="width: 25%;">${formatIndianNumber(
              totalTaxableAmount,
            )}</td>
          </tr>
          <tr>
            <td class="summary-label"></td>
            <td class="summary-label">SGST Amount</td>
            <td class="summary-value">${formatIndianNumber(
              totalSGSTAmount,
            )}</td>
          </tr>
           <tr>
            <td class="summary-label"></td>
            <td class="summary-label">CGST Amount</td>
            <td class="summary-value">${formatIndianNumber(
              totalCGSTAmount,
            )}</td>
          </tr>
          <tr>
            <td class="summary-label"></td>
            <td class="summary-label">IGST Amount</td>
            <td class="summary-value">${formatIndianNumber(
              totalIGSTAmount,
            )}</td>
          </tr>
          <tr>
            <td class="summary-label"></td>
            <td class="summary-label summary-bold">Total Invoice Value</td>
            <td class="summary-value summary-bold">${formatIndianNumber(
              totalInvoiceValue,
            )}</td>
          </tr>
        </table>
        
        <div class="total-words">
          <div>TOTAL INVOICE AMOUNT (in words)</div>
          <div>${amountInWords(totalInvoiceValue)}</div>
        </div>
        
        <div class="combined-footer-container">
          <div class="info-content">
            <p class="font-bold">Terms & Conditions</p>
            <p>Delivery within next 7 days</p>
            <p>Freight and loading/unloading charges included in the above cost</p>
            <p>Payment Terms - Advance</p>
          </div>
          
          <div class="signature-container">
             <img src="${
               poData?.invoice_data.warehouse?.signature_image || ''
             }" alt="Signature" class="signature-image"/>
            <div class="signature-details">
              <div>(Signature)</div>
              <div>${poData?.invoice_data?.warehouse?.party_name}</div>
              <div>Authorised Person: ${
                poData?.invoice_data?.warehouse?.purchase_order_invoice_address
                  ?.name
              }</div>
              <div>Designation: Finance Manager</div>
            </div>
          </div>
        </div>
      </div>
    </body>
    </html>
  `;
};

export const generatePOPdf = paData => {
  const content = createPOTemplate(paData);
  openAndPrint(content);
};

// PO Excel
// Builds the same PO invoice as `generatePOPdf` but as an .xlsx download,
// matching the column layout of the reference "Innisfree PO" sheet.
// MRP column is intentionally left blank until the backend exposes it on
// PurchaseOrderInvoiceLine.
export const generatePOExcel = async poData => {
  const { default: ExcelJS } = await import('exceljs');

  const invoiceData = poData?.invoice_data;
  const lines = invoiceData?.invoice_lines || [];

  const toNumber = value => parseFloat(value) || 0;

  // Prefix a leading =, +, -, @ so Excel treats the value as text, not a formula.
  const sanitizeExcelText = value => {
    const text = String(value ?? '');
    return /^[=+\-@]/.test(text) ? `'${text}` : text;
  };

  const totalQuantity = lines.reduce(
    (sum, line) => sum + toNumber(line?.quantity),
    0,
  );
  const totalTaxableAmount = lines.reduce(
    (sum, line) => sum + toNumber(line.taxable_amount),
    0,
  );
  const totalIGSTAmount = lines.reduce(
    (sum, line) => sum + toNumber(line.igst_tax_amount),
    0,
  );
  const totalCGSTAmount = lines.reduce(
    (sum, line) => sum + toNumber(line.cgst_tax_amount),
    0,
  );
  const totalSGSTAmount = lines.reduce(
    (sum, line) => sum + toNumber(line.sgst_tax_amount),
    0,
  );
  const totalInvoiceValue =
    totalTaxableAmount + totalIGSTAmount + totalCGSTAmount + totalSGSTAmount;

  const warehouse = invoiceData?.warehouse;
  const vendor = invoiceData?.vendor;
  const warehouseAddress = warehouse?.purchase_order_invoice_address;

  const buildBuyerDetails = () =>
    [
      warehouse?.party_name || '',
      `Address : ${warehouseAddress?.address_line_1 || ''}`,
      warehouseAddress?.address_line_2 || '',
      `${warehouseAddress?.city || ''}, ${warehouseAddress?.state || ''}, ${
        warehouseAddress?.pincode || ''
      }`,
      `State : ${warehouseAddress?.state || ''}`,
      `Contact No : ${warehouseAddress?.phone || '-'}`,
      `Email ID : ${warehouseAddress?.email || '-'}`,
      `GSTIN : ${warehouse?.gstin || '-'}`,
    ]
      .filter(part => part !== '')
      .join('\n');

  const buildSupplierDetails = () =>
    [
      vendor?.name || '',
      `Address : ${vendor?.contact_address_line_1 || ''}`,
      vendor?.contact_address_line_2 || '',
      `${vendor?.contact_city || ''}, ${vendor?.contact_state || ''}, ${
        vendor?.contact_pincode || ''
      }`,
      `State : ${vendor?.contact_state || ''}`,
      `Contact No : ${vendor?.contact_phone || '-'}`,
      `Email ID : ${vendor?.contact_email || '-'}`,
      `GSTIN : ${vendor?.gstin || '-'}`,
    ]
      .filter(part => part !== '')
      .join('\n');

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('PO');

  const COLUMN_COUNT = 14; // A-N
  const BUYER_LAST_COL = 7; // A-G buyer / H-N supplier
  const NUMBER_FORMAT = '#,##,##0.00';
  const PERCENT_FORMAT = '0.##%';

  const thinBorder = {
    top: { style: 'thin' },
    left: { style: 'thin' },
    bottom: { style: 'thin' },
    right: { style: 'thin' },
  };

  const applyBorders = (rowNumber, fromCol = 1, toCol = COLUMN_COUNT) => {
    const row = sheet.getRow(rowNumber);
    for (let col = fromCol; col <= toCol; col++) {
      row.getCell(col).border = thinBorder;
    }
  };

  // Title
  sheet.mergeCells(1, 1, 1, COLUMN_COUNT);
  const titleCell = sheet.getCell(1, 1);
  titleCell.value = `${warehouse?.party_name || ''} PO #${
    poData?.po_code || ''
  }`;
  titleCell.font = { bold: true, size: 14 };
  titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
  applyBorders(1);

  // Buyer / Supplier header
  sheet.mergeCells(2, 1, 2, BUYER_LAST_COL);
  sheet.mergeCells(2, BUYER_LAST_COL + 1, 2, COLUMN_COUNT);
  const buyerHeader = sheet.getCell(2, 1);
  buyerHeader.value = "Buyer's Details";
  const supplierHeader = sheet.getCell(2, BUYER_LAST_COL + 1);
  supplierHeader.value = "Supplier's Details";
  [buyerHeader, supplierHeader].forEach(cell => {
    cell.font = { bold: true };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
  });
  applyBorders(2);

  // Buyer / Supplier address blocks
  sheet.mergeCells(3, 1, 3, BUYER_LAST_COL);
  sheet.mergeCells(3, BUYER_LAST_COL + 1, 3, COLUMN_COUNT);
  const buyerCell = sheet.getCell(3, 1);
  buyerCell.value = buildBuyerDetails();
  const supplierCell = sheet.getCell(3, BUYER_LAST_COL + 1);
  supplierCell.value = buildSupplierDetails();
  [buyerCell, supplierCell].forEach(cell => {
    cell.alignment = { wrapText: true, vertical: 'top' };
  });
  applyBorders(3);

  // Items table header (two rows)
  const headerTopRow = 4;
  const headerBottomRow = 5;

  const singleRowHeaders = [
    { col: 1, label: 'S. No' },
    { col: 2, label: 'Description of Goods' },
    { col: 3, label: 'SKU' },
    { col: 4, label: 'HSN for service' },
    { col: 5, label: 'Qty' },
    { col: 6, label: 'Units' },
    { col: 7, label: 'MRP' },
    { col: 8, label: 'Taxable Price' },
  ];
  singleRowHeaders.forEach(({ col, label }) => {
    sheet.mergeCells(headerTopRow, col, headerBottomRow, col);
    const cell = sheet.getCell(headerTopRow, col);
    cell.value = label;
  });

  const taxGroups = [
    { label: 'SGST', startCol: 9 },
    { label: 'CGST', startCol: 11 },
    { label: 'IGST', startCol: 13 },
  ];
  taxGroups.forEach(({ label, startCol }) => {
    sheet.mergeCells(headerTopRow, startCol, headerTopRow, startCol + 1);
    sheet.getCell(headerTopRow, startCol).value = label;
    sheet.getCell(headerBottomRow, startCol).value = 'Rate';
    sheet.getCell(headerBottomRow, startCol + 1).value = 'Amount';
  });

  [headerTopRow, headerBottomRow].forEach(rowNumber => {
    const row = sheet.getRow(rowNumber);
    for (let col = 1; col <= COLUMN_COUNT; col++) {
      const cell = row.getCell(col);
      cell.font = { bold: true };
      cell.alignment = {
        horizontal: 'center',
        vertical: 'middle',
        wrapText: true,
      };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFF2F2F2' },
      };
    }
    applyBorders(rowNumber);
  });

  // Item rows
  let currentRow = headerBottomRow + 1;
  lines.forEach((line, index) => {
    const row = sheet.getRow(currentRow);
    row.getCell(1).value = index + 1;
    row.getCell(2).value = sanitizeExcelText(line.name);
    row.getCell(3).value = sanitizeExcelText(line.sku);
    row.getCell(4).value = sanitizeExcelText(line.hsn_code);
    row.getCell(5).value = line?.quantity ?? '';
    row.getCell(6).value = 'Pcs';
    row.getCell(7).value = toNumber(line.mrp);
    row.getCell(7).numFmt = NUMBER_FORMAT;
    row.getCell(8).value = toNumber(line.taxable_amount);
    row.getCell(8).numFmt = NUMBER_FORMAT;
    row.getCell(9).value = toNumber(line.sgst_tax_rate) / 100;
    row.getCell(9).numFmt = PERCENT_FORMAT;
    row.getCell(10).value = toNumber(line.sgst_tax_amount);
    row.getCell(10).numFmt = NUMBER_FORMAT;
    row.getCell(11).value = toNumber(line.cgst_tax_rate) / 100;
    row.getCell(11).numFmt = PERCENT_FORMAT;
    row.getCell(12).value = toNumber(line.cgst_tax_amount);
    row.getCell(12).numFmt = NUMBER_FORMAT;
    row.getCell(13).value = toNumber(line.igst_tax_rate) / 100;
    row.getCell(13).numFmt = PERCENT_FORMAT;
    row.getCell(14).value = toNumber(line.igst_tax_amount);
    row.getCell(14).numFmt = NUMBER_FORMAT;

    row.getCell(2).alignment = { horizontal: 'left', wrapText: true };
    for (let col = 1; col <= COLUMN_COUNT; col++) {
      row.getCell(col).font = { name: 'Arial', size: 8 };
    }
    applyBorders(currentRow);
    currentRow += 1;
  });

  // Total row
  const totalRow = currentRow;
  sheet.mergeCells(totalRow, 1, totalRow, 4);
  const totalLabelCell = sheet.getCell(totalRow, 1);
  totalLabelCell.value = 'Total';
  totalLabelCell.font = { bold: true };
  totalLabelCell.alignment = { horizontal: 'left' };
  sheet.getCell(totalRow, 5).value = totalQuantity;
  sheet.getCell(totalRow, 8).value = totalTaxableAmount;
  sheet.getCell(totalRow, 8).numFmt = NUMBER_FORMAT;
  sheet.getCell(totalRow, 10).value = totalSGSTAmount;
  sheet.getCell(totalRow, 10).numFmt = NUMBER_FORMAT;
  sheet.getCell(totalRow, 12).value = totalCGSTAmount;
  sheet.getCell(totalRow, 12).numFmt = NUMBER_FORMAT;
  sheet.getCell(totalRow, 14).value = totalIGSTAmount;
  sheet.getCell(totalRow, 14).numFmt = NUMBER_FORMAT;
  for (let col = 1; col <= COLUMN_COUNT; col++) {
    sheet.getCell(totalRow, col).font = { bold: true };
  }
  applyBorders(totalRow);
  currentRow += 1;

  // Summary rows
  const summaryRows = [
    { label: 'Taxable Amount', value: totalTaxableAmount },
    { label: 'SGST Amount', value: totalSGSTAmount },
    { label: 'CGST Amount', value: totalCGSTAmount },
    { label: 'IGST Amount', value: totalIGSTAmount },
    { label: 'Total Invoice Value', value: totalInvoiceValue, bold: true },
  ];
  summaryRows.forEach(({ label, value, bold }) => {
    sheet.mergeCells(currentRow, 1, currentRow, BUYER_LAST_COL);
    sheet.mergeCells(currentRow, BUYER_LAST_COL + 1, currentRow, COLUMN_COUNT);
    const labelCell = sheet.getCell(currentRow, 1);
    labelCell.value = label;
    labelCell.alignment = { horizontal: 'left' };
    const valueCell = sheet.getCell(currentRow, BUYER_LAST_COL + 1);
    valueCell.value = value;
    valueCell.numFmt = NUMBER_FORMAT;
    valueCell.alignment = { horizontal: 'right' };
    if (bold) {
      labelCell.font = { bold: true };
      valueCell.font = { bold: true };
    }
    applyBorders(currentRow);
    currentRow += 1;
  });

  // Amount in words
  sheet.mergeCells(currentRow, 1, currentRow, COLUMN_COUNT);
  const wordsCell = sheet.getCell(currentRow, 1);
  wordsCell.value = `TOTAL INVOICE AMOUNT (in words)\n${amountInWords(
    totalInvoiceValue,
  )}`;
  wordsCell.alignment = { wrapText: true, vertical: 'top' };
  applyBorders(currentRow);
  currentRow += 1;

  // Terms & Conditions
  sheet.mergeCells(currentRow, 1, currentRow, COLUMN_COUNT);
  const termsCell = sheet.getCell(currentRow, 1);
  termsCell.value = [
    'Terms & Conditions',
    'Delivery within next 7 days',
    'Freight and loading/unloading charges included in the above cost',
    'Payment Terms - Advance',
  ].join('\n');
  termsCell.alignment = { wrapText: true, vertical: 'top' };
  applyBorders(currentRow);
  currentRow += 1;

  // Signature
  sheet.mergeCells(currentRow, 1, currentRow, COLUMN_COUNT);
  const signatureCell = sheet.getCell(currentRow, 1);
  signatureCell.value = [
    '(Signature)',
    warehouse?.party_name || '',
    `Authorised Person: ${warehouseAddress?.name || ''}`,
    'Designation: Finance Manager',
  ].join('\n');
  signatureCell.alignment = {
    wrapText: true,
    vertical: 'top',
    horizontal: 'right',
  };
  applyBorders(currentRow);

  // Column widths
  const columnWidths = [6, 30, 16, 12, 6, 8, 12, 14, 8, 12, 8, 12, 8, 12];
  columnWidths.forEach((width, index) => {
    sheet.getColumn(index + 1).width = width;
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `PO_${poData?.po_code || 'invoice'}.xlsx`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(url);
};

const openAndPrint = (content, data, callback) => {
  const newTab = window.open('', '_blank');
  newTab.document.open();
  newTab.document.write(content);
  newTab.document.close();

  newTab.onload = () => {
    if (callback) {
      callback(newTab, data);
    }

    // close tab after action
    newTab.onafterprint = () => {
      newTab.close();
    };

    setTimeout(() => {
      newTab.print();
    }, 500);
  };
  newTab.focus();
};
